import React, { useRef, useEffect } from 'react';
import { BookOpen, Edit3, Lightbulb, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { SpinnerIcon } from '@/components/ui/SpinnerIcon';

interface MobileAnalysisDrawerProps {
  isOpen: boolean;
  isAnalyzing: boolean;
  analysisObject?: any;
  selectedText?: string;
  onClose: () => void;
}

export function MobileAnalysisDrawer({
  isOpen,
  isAnalyzing,
  analysisObject,
  selectedText,
  onClose
}: MobileAnalysisDrawerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new content is added (for streaming)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [analysisObject]);

  // Check if we have structured data
  const hasStructuredData = analysisObject && Object.keys(analysisObject).length > 0;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-6 w-6" />
              <span>AI Analysis</span>
            </div>
            {isAnalyzing && (
              <div className="flex items-center space-x-1">
                <SpinnerIcon size="sm" color="hsl(var(--primary))" />
                <span className="text-xs text-muted-foreground">Analyzing...</span>
              </div>
            )}
          </DrawerTitle>
          {/* <DrawerDescription className="text-left">
            Understanding this sentence structure and meaning
          </DrawerDescription> */}
        </DrawerHeader>
        
        <div ref={scrollRef} className="px-4 pb-4 max-h-[60vh] overflow-y-auto">
          {/* 原句 */}
          {selectedText && (
            <div className="bg-muted/30 rounded-lg p-3 mb-4">
              <div className="text-sm font-medium text-muted-foreground mb-2">Original Sentence</div>
              <div className="text-base font-medium">
                {selectedText}
              </div>
            </div>
          )}

          {isAnalyzing && !hasStructuredData && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <SpinnerIcon size="sm" color="hsl(var(--primary))" />
                <span className="text-sm">Analyzing sentence structure...</span>
              </div>
            </div>
          )}

          {hasStructuredData && (
            <div className="space-y-4">
              {/* Sentence Structure */}
              <AnimatePresence>
                {analysisObject?.sentenceStructure?.content && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-background/50 border border-border/50 rounded-2xl p-2 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 border border-foreground/60 rounded-lg flex-shrink-0">
                        <BookOpen className="h-4 w-4 text-foreground/60" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.sentenceStructure.title || '句子结构'}
                      </h4>
                    </div>
                    <div className="ml-8">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {analysisObject.sentenceStructure.content}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Grammar Analysis */}
              <AnimatePresence>
                {analysisObject?.grammarAnalysis?.content && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-background/50 border border-border/50 rounded-2xl p-2 space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 border border-foreground/60 rounded-lg flex-shrink-0">
                        <Lightbulb className="h-4 w-4 text-foreground/60" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.grammarAnalysis.title || '语法分析'}
                      </h4>
                    </div>
                    <div className="ml-8">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {analysisObject.grammarAnalysis.content}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Context Understanding */}
              <AnimatePresence>
                {analysisObject?.contextUnderstanding?.content && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-background/50 border border-border/50 rounded-2xl p-2 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 border border-foreground/60 rounded-lg flex-shrink-0">
                        <MessageSquare className="h-4 w-4 text-foreground/60" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.contextUnderstanding.title || '语境理解'}
                      </h4>
                    </div>
                    <div className="ml-8">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {analysisObject.contextUnderstanding.content}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Possible Rewrites */}
              <AnimatePresence>
                {analysisObject?.possibleRewrites?.suggestions && analysisObject.possibleRewrites.suggestions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-background/50 border border-border/50 rounded-2xl p-2 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 border border-foreground/60 rounded-lg flex-shrink-0">
                        <Edit3 className="h-4 w-4 text-foreground/60" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.possibleRewrites.title || '可能的改写'}
                      </h4>
                    </div>
                    <div className="ml-4 space-y-3">
                      {analysisObject.possibleRewrites.suggestions.map((suggestion: any, index: number) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          className="text-sm"
                        >
                          <div className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              {suggestion.rewrite || suggestion.text ? (
                                <p className="font-medium text-foreground">
                                  {suggestion.rewrite || suggestion.text}
                                </p>
                              ) : null}
                              {suggestion.explanation && (
                                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                                  {suggestion.explanation}
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {!isAnalyzing && !hasStructuredData && (
            <div className="text-center py-4 text-gray-500">
              No analysis available
            </div>
          )}
        </div>
        
        <DrawerFooter>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="lg"
              className="flex-1 rounded-2xl"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
